import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Form, Button, Alert, Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatErrorMessage } from '../utils/errorHandling';
import BackgroundShapes from '../components/common/BackgroundShapes';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setFetchError('No invitation token provided.');
      setLoading(false);
      return;
    }

    authAPI.getInviteInfo(token)
      .then(res => setInviteInfo(res.data))
      .catch(err => setFetchError(formatErrorMessage(err, 'This invitation is invalid or has expired.')))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!inviteInfo.already_registered) {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await authAPI.acceptInvite({
        token,
        name: inviteInfo.already_registered ? '' : name,
        password,
      });
      localStorage.setItem('token', res.data.access_token);
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to accept invitation. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page">
        <BackgroundShapes />
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="auth-page">
        <BackgroundShapes />
        <motion.nav className="landing-nav" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Container>
            <div className="nav-content">
              <Link to="/" className="nav-logo">
                <img src={logo} alt="ChangeFox" className="nav-logo-img" />
              </Link>
            </div>
          </Container>
        </motion.nav>
        <motion.div className="auth-container" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="glass-card auth-card text-center">
            <h1 className="auth-title mb-3">Invitation Error</h1>
            <Alert variant="danger">{fetchError}</Alert>
            <Link to="/login"><Button className="btn-auth-primary mt-3">Go to Login</Button></Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SEO title="Accept Invitation - ChangeFox" description="Accept your invitation to join a workspace on ChangeFox." path="/accept-invite" />
      <BackgroundShapes />

      <motion.nav className="landing-nav" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
          </div>
        </Container>
      </motion.nav>

      <motion.div className="auth-container" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="glass-card auth-card">
          <div className="text-center mb-4">
            <motion.h1 className="auth-title" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
              You're invited!
            </motion.h1>
            <p className="auth-subtitle mb-0">
              <strong>{inviteInfo.inviter_name}</strong> invited you to join <strong>{inviteInfo.workspace_name}</strong>
            </p>
          </div>

          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <Alert variant="danger" className="mb-4">{error}</Alert>
            </motion.div>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                value={inviteInfo.email}
                disabled
                className="auth-input"
              />
            </Form.Group>

            {inviteInfo.already_registered ? (
              <>
                <p className="text-muted text-center mb-3">
                  You already have an account. Enter your password to join the workspace.
                </p>
                <Form.Group className="mb-4">
                  <Form.Control
                    type="password"
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="auth-input"
                  />
                </Form.Group>
              </>
            ) : (
              <>
                <p className="text-muted text-center mb-3">
                  Create your account to join the workspace.
                </p>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="auth-input"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="password"
                    placeholder="Create password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="auth-input"
                  />
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Control
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="auth-input"
                  />
                </Form.Group>
              </>
            )}

            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
              <Button type="submit" className="btn-auth-primary w-100" disabled={submitting}>
                {submitting ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {inviteInfo.already_registered ? 'Log In & Join Workspace' : 'Create Account & Join Workspace'}
              </Button>
            </motion.div>
          </Form>

          <p className="text-center text-muted mt-4 mb-0">
            {inviteInfo.already_registered ? (
              <>Forgot your password? <Link to="/login" className="auth-link">Go to Login</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="auth-link">Sign in</Link></>
            )}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AcceptInvite;
